import { useEffect, useRef, useState } from "react";
import { PARTY, LAYERS, fmtTime } from "./shared";
import { UsMap } from "./UsMap";
import { StatePanel } from "./StatePanel";
import { PartyLogo } from "./PartyLogo";

function Legend() {
  const sw = (bg, label) => (
    <span className="flex items-center gap-2"><span className="w-3 h-3 inline-block" style={{ background: bg }} />{label}</span>
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-[10px] uppercase tracking-[0.18em] text-white/60" data-testid="midterms-map-legend">
      {sw(PARTY.D.solid, "Dem won")}
      {sw(PARTY.D.tint, "Dem leads")}
      {sw(PARTY.R.solid, "Rep won")}
      {sw(PARTY.R.tint, "Rep leads")}
      {sw("rgba(255,255,255,0.22)", "Awaiting")}
      {sw("rgba(255,255,255,0.05)", "No race")}
    </div>
  );
}

function LatestCalls({ calls }) {
  return (
    <div className="border border-white/15 bg-white/[0.04] backdrop-blur-md p-5 md:p-6" data-testid="midterms-latest-calls">
      <div className="overline text-white/60 mb-1">Latest calls</div>
      <p className="text-xs text-white/40 mb-4">Tap any state on the map for its full results.</p>
      {calls.length === 0 ? (
        <p className="text-sm text-white/60 font-serif-ed italic">
          No races called yet. Calls stream in here automatically once polls close on November 3.
        </p>
      ) : (
        <ul className="divide-y divide-white/10">
          {calls.map((c) => (
            <li key={c.id} className="py-3 flex items-center gap-3" data-testid={`midterms-call-${c.id}`}>
              <PartyLogo party={c.called_party} size={28} />
              <div className="flex-1 min-w-0">
                <div className="font-serif-ed text-base leading-tight truncate">
                  {c.state_name} <span className="text-white/40 text-sm capitalize">· {c.type}</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/50 truncate">
                  {c.winner || PARTY[c.called_party]?.name} · {c.status === "projected" ? "projected" : "called"} {fmtTime(c.updated_at)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function MapExplorer({ hub }) {
  const [layer, setLayer] = useState("senate");
  const [selected, setSelected] = useState(null);
  const sideRef = useRef(null);
  const map = hub?.map || {};

  useEffect(() => {
    if (selected && window.innerWidth < 1024 && sideRef.current) {
      const header = document.querySelector('[data-testid="site-header"]')?.offsetHeight || 0;
      const top = sideRef.current.getBoundingClientRect().top + window.scrollY - header - 16;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [selected]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="midterms-map-explorer">
      <div className="lg:col-span-3">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="overline text-white/60">The map</div>
          <div className="flex border border-white/20" role="tablist" data-testid="midterms-layer-tabs">
            {LAYERS.map((l) => (
              <button
                key={l.id}
                role="tab"
                aria-selected={layer === l.id}
                onClick={() => setLayer(l.id)}
                className={`px-3 sm:px-4 py-2 md:py-3.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${
                  layer === l.id ? "bg-white text-[#07112e]" : "text-white/70 hover:text-white"
                }`}
                data-testid={`midterms-layer-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <UsMap layer={map[layer] || {}} layerLabel={layer === "house" ? "House" : layer === "senate" ? "Senate" : "governor"} selected={selected} onSelect={setSelected} />
        <Legend />
      </div>
      <div className="lg:col-span-2" ref={sideRef}>
        {selected ? (
          <StatePanel code={selected} onClose={() => setSelected(null)} />
        ) : (
          <LatestCalls calls={hub?.latest_calls || []} />
        )}
      </div>
    </div>
  );
}
